import { Decimal } from 'decimal.js';
import { Fragment, useMemo } from 'react';

import type { PositionReplayDto, PositionReplayExecutionDto } from '../../lib/api/endpoints';
import { formatCurrency } from '../../lib/format/currency';

interface ReplayExecutionListProps {
  currency: string;
  replay: PositionReplayDto;
}

interface ExecutionGroup {
  key: string;
  episodeId: number;
  date: string;
  side: 'BUY' | 'SELL';
  quantity: string;
  averagePrice: string;
  executions: PositionReplayExecutionDto[];
}

function compareExecutions(
  left: PositionReplayExecutionDto,
  right: PositionReplayExecutionDto,
): number {
  const dateOrder = left.date.localeCompare(right.date);
  if (dateOrder !== 0) return dateOrder;
  if (left.tradeTime && right.tradeTime) {
    const timeOrder = left.tradeTime.localeCompare(right.tradeTime);
    if (timeOrder !== 0) return timeOrder;
  } else if (left.tradeTime) {
    return -1;
  } else if (right.tradeTime) {
    return 1;
  }
  return left.id.localeCompare(right.id);
}

export function groupReplayExecutions(
  executions: readonly PositionReplayExecutionDto[],
): ExecutionGroup[] {
  const grouped = new Map<string, PositionReplayExecutionDto[]>();
  for (const execution of [...executions].sort(compareExecutions)) {
    const key = `${execution.episodeId}:${execution.date}:${execution.side}`;
    const fills = grouped.get(key) ?? [];
    fills.push(execution);
    grouped.set(key, fills);
  }

  return [...grouped.entries()].map(([key, fills]) => {
    const first = fills[0];
    if (!first) throw new Error('Replay execution group unexpectedly empty.');
    const quantity = fills.reduce((total, fill) => total.plus(fill.quantity), new Decimal(0));
    const notional = fills.reduce(
      (total, fill) => total.plus(new Decimal(fill.quantity).mul(fill.price)),
      new Decimal(0),
    );
    return {
      key,
      episodeId: first.episodeId,
      date: first.date,
      side: first.side,
      quantity: quantity.toString(),
      averagePrice: quantity.isZero() ? '0' : notional.div(quantity).toString(),
      executions: fills,
    };
  });
}

export function formatFlatDuration(days: number): string {
  if (days === 0) return 'Reopened without a flat day';
  if (days < 7) return `${days} ${days === 1 ? 'day' : 'days'} flat`;
  const weeks = Math.round(days / 7);
  return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} flat`;
}

function executionTime(execution: PositionReplayExecutionDto): string {
  if (!execution.tradeTime) return 'Approximate time';
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(execution.tradeTime));
}

export function ReplayExecutionList({ currency, replay }: ReplayExecutionListProps) {
  const groups = useMemo(() => groupReplayExecutions(replay.executions), [replay.executions]);
  const episodes = useMemo(
    () => new Map(replay.episodes.map((episode) => [episode.id, episode])),
    [replay.episodes],
  );
  let priorEpisodeId: number | null = null;

  return (
    <div className="overflow-x-auto rounded border border-border">
      <table className="min-w-full divide-y divide-border text-sm">
        <thead className="bg-bg-muted text-left text-xs uppercase tracking-normal text-text-muted">
          <tr>
            <th className="px-3 py-2" scope="col">
              Date
            </th>
            <th className="px-3 py-2" scope="col">
              Side
            </th>
            <th className="px-3 py-2" scope="col">
              Quantity
            </th>
            <th className="px-3 py-2" scope="col">
              Average price
            </th>
            <th className="px-3 py-2" scope="col">
              Fills
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border bg-surface">
          {groups.map((group) => {
            const episode = episodes.get(group.episodeId);
            const showBoundary =
              replay.scope === 'full' &&
              priorEpisodeId !== null &&
              priorEpisodeId !== group.episodeId &&
              episode?.flatDaysBefore !== null &&
              episode?.flatDaysBefore !== undefined;
            priorEpisodeId = group.episodeId;
            return (
              <Fragment key={group.key}>
                {showBoundary ? (
                  <tr>
                    <td
                      className="border-y border-primary/40 bg-primary/10 px-3 py-2 text-center font-medium text-primary"
                      colSpan={5}
                    >
                      Episode {group.episodeId} · {formatFlatDuration(episode.flatDaysBefore ?? 0)}
                    </td>
                  </tr>
                ) : null}
                <tr>
                  <td className="whitespace-nowrap px-3 py-3">{group.date}</td>
                  <td
                    className={
                      group.side === 'BUY'
                        ? 'whitespace-nowrap px-3 py-3 font-semibold text-success'
                        : 'whitespace-nowrap px-3 py-3 font-semibold text-danger'
                    }
                  >
                    {group.side}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 tabular-nums">{group.quantity}</td>
                  <td className="whitespace-nowrap px-3 py-3 tabular-nums">
                    {formatCurrency(group.averagePrice, currency)}
                  </td>
                  <td className="min-w-48 px-3 py-3">
                    {group.executions.length === 1 ? (
                      <span
                        className={group.executions[0]?.timeKnown ? 'text-text-muted' : 'text-warn'}
                      >
                        {group.executions[0] ? executionTime(group.executions[0]) : ''}
                      </span>
                    ) : (
                      <details>
                        <summary className="cursor-pointer text-primary hover:text-primary-hover">
                          {group.executions.length} fills
                        </summary>
                        <ol className="mt-2 space-y-1 border-l border-border pl-3">
                          {group.executions.map((execution) => (
                            <li key={execution.id} className="tabular-nums text-text-muted">
                              {executionTime(execution)} · {execution.quantity} @{' '}
                              {formatCurrency(execution.price, currency)}
                            </li>
                          ))}
                        </ol>
                      </details>
                    )}
                  </td>
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
